/** @jsx jsx */

import {
  Fragment as _,
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useContext,
  RefObject,
} from 'react';
import { css, jsx } from '@emotion/core';
import { useSelector, useDispatch } from 'react-redux';
import getBlockById from 'services/apiHelpers/getBlockById';
import { createContext } from 'react';
import getStore from 'services/getStore';
import getCurrentTopic from 'services/apiHelpers/getCurrentTopic';
import Wrapper from './Wrapper';
import HiddenBackButton from './HiddenBackButton';
import BackgroundImage from './Background';
import next from './next';
import updateReadOnlyText from 'services/updateReadOnlyText';
import pubSub from 'services/pubSub';
import { createSelector } from 'reselect';
import headingsStyles from './headingsStyles';
import { MenuContainer } from './MenuContainer';
import { State } from 'store/store';
import { controlAudioAction } from 'actions/controlAudioAction';
import { useHeader } from 'services/useHeader';
import getTopics from 'services/apiHelpers/getTopics';
import { getIsFirstInteraction } from 'services/getIsFirstInteraction';
import { ModuleContext } from 'components/Module/Module_v2';
import { InfoBubbleContainer } from 'components/Editor/EditorUtilities/UI/Bubble';
import { moduleNavigationActions } from 'components/NavigationSettings/ModuleNavigation';
import { updateInteractionsOpenAction } from 'actions/updateInteractionsOpenAction';
import { buttonLayouts } from './buttonLayouts';
import { updateGlobalFontStylesFromTheme } from 'styles/updateGlobalFontStylesFromTheme';
import './highlightActiveElement';
import { Orbs, getAnimatedOrbsDefaultValues } from 'components/Module/Orbs';
import { svgStringEncode } from 'services/svgStringEncode';

import {
  Clouds,
  cloudsSVGToString,
} from 'components/Editor/OnboardingWizard/Clouds';
import {
  Squiggle,
  squiggleSVGToString,
} from 'components/Editor/OnboardingWizard/Squiggle';
import {
  Waves as Hills,
  wavesSVGToString as hillsSVGToString,
} from 'components/Editor/OnboardingWizard/Waves';
import {
  Bubbles,
  bubblesSVGToString,
} from 'components/Editor/OnboardingWizard/Bubbles';
import { Arc, arcSVGToString } from 'components/Editor/OnboardingWizard/Arc';
import { Flow, flowSVGToString } from 'components/Editor/OnboardingWizard/Flow';

const chameleonBackgrounds = {
  Clouds,
  Squiggle,
  Hills,
  Bubbles,
  Arc,
  Flow,
};

const chameleonBackgroundSVGStrings = {
  Clouds: cloudsSVGToString,
  Squiggle: squiggleSVGToString,
  Hills: hillsSVGToString,
  Bubbles: bubblesSVGToString,
  Arc: arcSVGToString,
  Flow: flowSVGToString,
};

const store = getStore();
const renderCount = {};

const AUDIO_FILE_DEFAULT =
  'https://freesound.org/data/previews/542/542450_5674468-lq.mp3';

export const InteractionContext = createContext<any>(undefined);

const getInteractionsOpen = createSelector(
  (state: any) => state.interactionsOpen,
  interactionsOpen => interactionsOpen
);

const getHiddenBackButton = (
  section: HTMLElement,
  id: string
): HTMLButtonElement => {
  const hiddenBackButton = section?.querySelector(
    `#hidden-back-button-${id}`
  ) as HTMLButtonElement;

  return hiddenBackButton;
};
//

const getHiddenMenuButton = (
  section: HTMLElement,
  id: string
): HTMLButtonElement => {
  const hiddenMenuButton = section?.querySelector(
    `#hidden-button-menu-${id}`
  ) as HTMLButtonElement;

  return hiddenMenuButton;
};

const defaultChameleonBackgroundColor = '#4EA3F7';

let topics;

const ContainerLogical = (props: any) => {
  const moduleContext = useContext(ModuleContext);
  const { exit, setComplete, setCompleteAndExit, resetModule } = moduleContext;

  let {
    id,
    name,
    style,
    children,
    lockDown,
    includeAudio = false,
    audioSrc,
    audioIdV2,
    autoPlay = false,
    stopAudioWhenModalIsOpened = true,
    controls = true,
    delay = 1,
    autoOpenNextSection = false,
    //

    chameleonBackground,
  } = props;

  // this will be updated on the fly and set to 'backgroundImage'
  // when a backgroundImagePlaceholder is included in props
  let backgroundStyle = props.backgroundStyle;

  const chameleonBackgroundColor =
    props?.chameleonBackgroundColor || defaultChameleonBackgroundColor;

  const {
    primaryOrbColor,
    secondaryOrbColor,
    tertiaryOrbColor,
    orbOpacity,
    orbBlurStrength,
    orbSpeed,
  } = getAnimatedOrbsDefaultValues(props);

  const { focusedNodeId, currentInteraction, theme } = useSelector(
    (state: State) => {
      return state;
    }
  );

  const dispatch = useDispatch();

  // check if nextInteractionMap is in 'order'
  // for the moment we'll use this dirty check

  //
  if (!topics) {
    topics = getTopics();
  }

  const isFirstInteractionOfModule = getIsFirstInteraction(topics, id);

  const { isFirstInteractionOfTopic, isLastInteractionOfTopic } =
    props?.data || {};

  const [tabbableElements, setTabbableElements] = useState<
    HTMLElement[] | undefined
  >();

  const lastTabbableElement = tabbableElements?.length
    ? tabbableElements[tabbableElements.length - 1]
    : null;

  let {
    sId,
    background = undefined,
    backgroundImage = undefined, // legacy
    backgroundImageOpacity = 100,
    backgroundBlur = 0,
    src = undefined,
    textColor = style.foreground,
  } = getBlockById(id).props as any;

  const {
    backgroundGradient,
    backgroundSecondaryColor,
    backgroundGradientAngle = 0,
    fullWidth = false,
  } = props;

  const ChameleonBackground =
    !backgroundImage && chameleonBackground
      ? chameleonBackgrounds[chameleonBackground]
      : null;

  const chameleonBackgroundSVGEncoded = chameleonBackgroundSVGStrings[
    chameleonBackground
  ]
    ? svgStringEncode(
        chameleonBackgroundSVGStrings[chameleonBackground](
          chameleonBackgroundColor
        )
      )
    : '';

  let buttonPosition = {
    left: 'start',
    center: 'center',
    right: 'end',
  }[props?.buttonPosition || 'center'];

  const linearGradient = backgroundGradient
    ? `linear-gradient(${background}, ${backgroundSecondaryColor})`
    : 'none';

  let interactionsOpen = useSelector(getInteractionsOpen);
  let isOpen = props.isOpen || interactionsOpen.includes(id);

  //

  let backgroundImagePlaceholder;

  if (backgroundStyle === undefined && props.backgroundImagePlaceholder) {
    backgroundStyle = 'backgroundImage';
    backgroundImagePlaceholder = props.backgroundImagePlaceholder;
  }

  if (src || backgroundImagePlaceholder) {
    backgroundImage = `url('${src ||
      backgroundImagePlaceholder}') center / cover no-repeat`;
  }

  const section: any = useRef<HTMLElement>(null);
  const selected = id === focusedNodeId;

  // the row what contains nav buttons. . .
  const navigationButtonsContainer = section?.current?.querySelector(
    `#i${id}-content-container > div:last-child`
  ) as HTMLDivElement;

  // check if there are any nav buttons in the navigationButtonsContainer
  const hasNavigationButton = !!navigationButtonsContainer?.querySelectorAll(
    '[class*="navigationButton"]'
  )?.length;

  const header = useHeader();
  const { headerStyle } = header.props;
  const { backButton, topicMenu } = header[headerStyle].props;

  let { headerHeight, isFixedHeaderHeight } = header[headerStyle].props;

  if (window.dev()) {
    headerHeight = isFixedHeaderHeight
      ? headerHeight
      : !isFirstInteractionOfModule
      ? 70
      : headerHeight;
  }

  const currentTopic = getCurrentTopic();

  // preference textColor coming from interaction > textColor coming from topic
  const textColorFromInteractionOrTopic =
    textColor ||
    (currentTopic && getBlockById(currentTopic.id)?.props?.textColor);

  const { fontSize = 16, lineHeight = 1.5, headings } = theme || {};

  const { h1, h2, h3, h4, h5, h6 } = headings || {};

  const contentContainer = useRef<HTMLDivElement>(null);

  const tabDirection = useRef<'next' | 'prev' | undefined>();
  const hiddenButtonForAccessibilityNavNext = useRef<HTMLAnchorElement>(null);

  useLayoutEffect(() => {
    if (!section.current) {
      section.current = document.querySelector(`#i${id}`);
    }

    !window.dev() && updateReadOnlyText(section.current);
  }, [textColorFromInteractionOrTopic]);

  useEffect(() => {
    if (!section?.current) {
      return;
    }

    const hiddenBackButton = getHiddenBackButton(section.current, id);

    if (!hiddenBackButton) {
      return;
    }

    const onKeyDown = (e: any) => {
      const { key, shiftKey } = e;

      if (key === 'Tab' && shiftKey) {
        e.preventDefault();
        //

        tabDirection.current = 'prev';

        if (hiddenButtonForAccessibilityNavNext.current) {
          hiddenButtonForAccessibilityNavNext.current.focus({
            preventScroll: true,
          });
          //
          tabDirection.current = undefined;
        }
      }
    };

    hiddenBackButton.addEventListener('keydown', onKeyDown);

    return () => {
      hiddenBackButton.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!section?.current) {
      return;
    }

    const onKeyDown = (e: any) => {
      const { key, shiftKey } = e;

      if (key === 'Tab' && !shiftKey) {
        e.preventDefault();

        const hiddenBackButton = getHiddenBackButton(section.current, id);
        const hiddenMenuButton = getHiddenMenuButton(section.current, id);

        if (hiddenBackButton && !hiddenBackButton.disabled) {
          //
          hiddenBackButton.focus({ preventScroll: true });
        } else {
          hiddenMenuButton ? hiddenMenuButton.focus() : section.current.focus();
        }
      }
    };

    lastTabbableElement?.addEventListener('keydown', onKeyDown);

    return () => {
      lastTabbableElement?.removeEventListener('keydown', onKeyDown);
    };
  }, [lastTabbableElement]);

  useEffect(() => {
    if (!lockDown) {
      return;
    }
  });

  useEffect(() => {
    if (!section.current) {
      return;
    }

    let tick = setInterval(() => {
      if (!section.current) {
        return;
      }
      //
      var scrollBarWidth = window.innerWidth - document.body.clientWidth;

      if (scrollBarWidth > 0) {
        clearInterval(tick);
        // if scrollBarWidth calcs to greater than 35
        // then wtf. wrong things are happening. don't update the padding
        // but if we're within that range. . .
        if (scrollBarWidth <= 35) {
          section.current.style.paddingLeft = `${scrollBarWidth}px`;
        }
      }
    }, 1000 / 12);

    return () => {
      clearInterval(tick);
    };
  }, []);

  useEffect(() => {
    if (window.dev() || !contentContainer.current) {
      return;
    }
    //
    // this is gonna get put someplace else
    // make it part of the hashChange maybe
    // but needs reference to the interaction sId/id
    //

    const anchorNavNextExit = e => {
      const elementsUnderClick = Array.from(
        document.elementsFromPoint(e.clientX, e.clientY)
      );

      const a = elementsUnderClick.find(el => el.tagName.toLowerCase() === 'a');

      const href = a?.getAttribute('href');

      if (!href) {
        return;
      }

      const {
        NEXT,
        EXIT,
        SET_MODULE_COMPLETE,
        SET_MODULE_COMPLETE_AND_EXIT,
        RESET_MODULE,
      } = moduleNavigationActions;

      const aNext = next(
        sId,
        id,
        props?.data?.isFirstInteractionOfTopic,
        props?.data?.isLastInteractionOfTopic
      );

      if ([NEXT, EXIT].includes(href)) {
        e.preventDefault();

        aNext(href);
        return;
      }

      if (
        [
          SET_MODULE_COMPLETE,
          SET_MODULE_COMPLETE_AND_EXIT,
          RESET_MODULE,
        ].includes(href)
      ) {
        //
        e.preventDefault();

        href === SET_MODULE_COMPLETE && setComplete();
        href === SET_MODULE_COMPLETE_AND_EXIT && setCompleteAndExit();
        href === RESET_MODULE && resetModule();
        return;
      }
    };

    contentContainer.current.addEventListener('click', anchorNavNextExit);

    return () => {
      contentContainer.current &&
        contentContainer.current.removeEventListener(
          'click',
          anchorNavNextExit
        );
    };
  }, []);

  useEffect(() => {
    if (window.dev()) {
      const data = includeAudio
        ? {
            id,
            audioSrc,
            autoPlay: window.dev() ? false : autoPlay,
            controls,
            delay,
          }
        : null;

      dispatch(controlAudioAction(data));
    }
  }, [includeAudio, audioSrc]);

  useEffect(() => {
    if (window.dev()) {
      return;
    }

    const sectionInViewSubscriber = pubSub.subscribe(
      'SECTION_IN_VIEW',
      ({ sectionInView }) => {
        if (sectionInView.id === id) {
          const data = includeAudio
            ? {
                id,
                audioSrc,
                autoPlay: window.dev() ? false : autoPlay,
                controls,
                delay,
                pause: false,
              }
            : null;

          dispatch(controlAudioAction(data));
        }
      }
    );

    return () => {
      sectionInViewSubscriber();
    };
  }, []);

  useEffect(() => {
    if (autoOpenNextSection) {
      store.dispatch(updateInteractionsOpenAction(id));
    }
  }, []);

  useEffect(() => {
    const tabbableElements = Array.from(
      section.current.querySelectorAll(
        'a[href], button, input:not([type="hidden"]), select, textarea, area[href], iframe, summary, [contenteditable="true"], [tabindex]:not([tabindex="-1"], span)'
      )
    ) as HTMLElement[];

    if (!tabbableElements) {
      return;
    }

    const lastTabbableElement = tabbableElements[tabbableElements.length - 1];

    // if the lastTabbableElement IS NOT a nav buttons in the ' footer '
    // we push hiddenButtonForAccessibilityNavNext into tabbableElements to ' take its place '
    if (!lastTabbableElement?.id?.includes('navigation-button')) {
      if (hiddenButtonForAccessibilityNavNext.current) {
        tabbableElements.push(hiddenButtonForAccessibilityNavNext.current);
      }
    }

    setTabbableElements(tabbableElements as HTMLElement[]);
  }, [open]);

  let isLocked = lockDown;

  // since the background gets set on the style on creation
  // kill it if we have a ' local ' background color
  // goal would be to remove it from the factory. but it's legacy from wizard
  // WINDING on down. . .
  const overideStyleBackground = props.hasOwnProperty('background')
    ? {
        background: background || '',
      }
    : {};

  if (!renderCount[id]) {
    renderCount[id] = 0;
  }

  // need to further work on all the concepts going on with the headerHeight
  // firstInteraction checking
  // responsive aspect etc
  // right now it's being spread over a couple places which feels super gross
  // need to bring it all under some unified solution

  let paddingTop = headerHeight;

  if (
    !isFirstInteractionOfModule &&
    !isFixedHeaderHeight &&
    headerStyle === 'graphic'
  ) {
    paddingTop = 70;
  }

  if (screen.availWidth <= 480) {
    paddingTop = 50;
  }

  // need to memoize this in output
  const updatedGlobalFontStylesFromTheme = updateGlobalFontStylesFromTheme({
    theme,
    textColorFromInteractionOrTopic,
    headingsStyles() {
      return headingsStyles({ h1, h2, h3, h4, h5, h6 });
    },
  });

  return (
    <InteractionContext.Provider
      value={{
        ...props.data,
        id,
        textColor: textColorFromInteractionOrTopic,
        stopAudioWhenModalIsOpened,
        next: next(
          sId,
          id,
          isFirstInteractionOfTopic,
          isLastInteractionOfTopic
        ),
        isLocked,
        children,
      }}
    >
      {/* @ts-ignore */}
      <section
        id={`i${id}`}
        css={css`
          position: relative;
          display: ${!isOpen ? 'none' : 'flex'};
          flex-direction: column;
          justify-content: center;
          align-items: center;

          padding-top: ${paddingTop}px;
          padding-bottom: 32px;

          width: 100%;
          min-height: 100vh;

          ${updatedGlobalFontStylesFromTheme}

          [id*='content-container'] > div:last-child {
            ${window.dev()
              ? `margin-${
                  {
                    start: 'left',
                    end: 'right',
                  }[buttonPosition]
                }: -9px;`
              : ''}

            > div {
              ${buttonLayouts(buttonPosition)}
            }
          }
        `}
        style={{
          position: 'relative',
          overflowX: 'hidden',
          ...{
            ...style,
            ...overideStyleBackground,
            ...(backgroundGradient && {
              background: `linear-gradient(${backgroundGradientAngle}deg, ${backgroundSecondaryColor}, ${background})`,
            }),
          },
        }}
        data-section
        data-section-text-color={textColorFromInteractionOrTopic}
        aria-label={`${props.name ? `${props.name}—` : ''}${
          props.sectionLabel
        }`}
        tabIndex={-1}
      >
        {[undefined, 'backgroundImage'].includes(backgroundStyle) &&
          backgroundImage && (
            <BackgroundImage
              key={backgroundImage}
              backgroundImage={backgroundImage}
              backgroundImageOpacity={backgroundImageOpacity}
              backgroundBlur={backgroundBlur}
            />
          )}

        {backgroundStyle === 'animatedOrbs' && (
          <Orbs
            {...{
              orbsFor: 'interaction',
              primaryOrbColor,
              secondaryOrbColor,
              tertiaryOrbColor,
              orbOpacity,
              orbBlurStrength,
              orbSpeed,
            }}
          />
        )}

        {backgroundStyle &&
          chameleonBackground &&
          backgroundStyle === chameleonBackground && (
            <img
              id={`chameleon-background-${id}`}
              src={chameleonBackgroundSVGEncoded}
              alt="Encoded SVG"
              css={css({
                position: 'absolute',
                left: 0,
                top: 0,
                width: '100vw',
                height: '100%',
                objectFit: 'cover',
              })}
            />

            // for the old times. . . {/* <ChameleonBackground color={chameleonBackgroundColor} /> */}
          )}

        {/* let's not even render this puppy if isFirstInteraction */}
        {!window.dev() && backButton && !isFirstInteractionOfModule ? (
          <HiddenBackButton id={id} />
        ) : null}

        {topicMenu && <MenuContainer id={id} rootSelector="#menu-portal" />}

        {/* @ts-ignore */}
        <div
          ref={contentContainer}
          id={`i${id}-content-container`}
          css={css({
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',

            width: '100%',
            maxWidth: fullWidth ? '100%' : '1100px',
            ...(includeAudio &&
              audioSrc &&
              controls && {
                '@media (max-width: 768px)': {
                  paddingBottom: 20,
                },
              }),
          })}
        >
          {children}
        </div>

        {isOpen && !hasNavigationButton ? (
          <a
            ref={hiddenButtonForAccessibilityNavNext}
            id={`hidden-button-for-accessibility-nav-next-${id}`}
            href="next"
            //
            style={{
              position: 'relative',
              padding: '10px 20px',
              opacity: 0,
              transition: 'opacity 0.3s',
              borderRadius: 8,
              border: `1px solid ${textColorFromInteractionOrTopic || '#000'}`,
              color: textColorFromInteractionOrTopic || '#000',
              fontSize: 14,
              textAlign: 'center',
              textDecoration: 'none',
              pointerEvents: 'none',
            }}
            onBlur={e => {
              e.currentTarget.style.opacity = '0';
            }}
            onFocus={e => {
              e.currentTarget.style.opacity = '1';
            }}
            onClick={e => {
              e.preventDefault();
              //

              e.currentTarget.style.opacity = '0';

              next(
                sId,
                id,
                isFirstInteractionOfTopic,
                isLastInteractionOfTopic
              )('next');
            }}
          >
            <span>Tab to return to top. Shift + Tab to return to content.</span>
            <br />
            <span>Press Enter or Space to continue.</span>
          </a>
        ) : null}

        {window.dev() && (
          <Wrapper
            id={id}
            focusedNodeId={focusedNodeId}
            headerHeight={headerHeight}
          />
        )}
      </section>

      <InfoBubbleContainer />
    </InteractionContext.Provider>
  );
};

export default ContainerLogical;
